import type { ReportBreakdown } from '../../../../types/reports';
import { formatBreakdownValue } from '../reportFormat';

/**
 * Full-width list of every item in a breakdown (used for "Expenses by
 * Category", where the user wants all categories, not just the top 8).
 */
export function BreakdownTable({ breakdown }: { breakdown: ReportBreakdown }) {
  const total = breakdown.items.reduce((acc, item) => acc + item.value, 0);

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-surface shadow-sm">
      <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
        <h3 className="text-sm font-semibold text-text">{breakdown.title}</h3>
        <span className="text-xs text-text-muted">
          {breakdown.items.length} {breakdown.items.length === 1 ? 'category' : 'categories'}
        </span>
      </div>
      <div className="max-h-[28rem] overflow-y-auto">
        <table className="w-full">
          <thead className="sticky top-0 bg-background/95 backdrop-blur z-10">
            <tr className="border-b border-border">
              <th className="px-4 py-2.5 text-left text-xs font-medium text-text-muted uppercase">Category</th>
              <th className="px-4 py-2.5 text-right text-xs font-medium text-text-muted uppercase whitespace-nowrap">
                Amount
              </th>
              <th className="px-4 py-2.5 text-right text-xs font-medium text-text-muted uppercase whitespace-nowrap">
                Share
              </th>
              <th className="px-4 py-2.5 w-40 text-left text-xs font-medium text-text-muted uppercase hidden sm:table-cell">
                Distribution
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {breakdown.items.map((item) => {
              const share = total ? (item.value / total) * 100 : 0;
              return (
                <tr key={item.label} className="hover:bg-background/50 transition-colors">
                  <td className="px-4 py-2.5 text-sm text-text">{item.label}</td>
                  <td className="px-4 py-2.5 text-sm text-text text-right tabular-nums whitespace-nowrap">
                    {formatBreakdownValue(item.value, breakdown.format)}
                  </td>
                  <td className="px-4 py-2.5 text-sm text-text-muted text-right tabular-nums whitespace-nowrap">
                    {share.toFixed(1)}%
                  </td>
                  <td className="px-4 py-2.5 hidden sm:table-cell">
                    <div className="h-1.5 w-full rounded-full bg-background overflow-hidden">
                      <div className="h-full rounded-full bg-primary/70" style={{ width: `${share}%` }} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t border-border bg-background/50">
              <td className="px-4 py-2.5 text-sm font-semibold text-text">Total</td>
              <td className="px-4 py-2.5 text-sm font-semibold text-text text-right tabular-nums whitespace-nowrap">
                {formatBreakdownValue(total, breakdown.format)}
              </td>
              <td className="px-4 py-2.5 text-sm text-text-muted text-right tabular-nums">100%</td>
              <td className="px-4 py-2.5 hidden sm:table-cell" />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
